import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, FileCode, Database, ChevronDown, ChevronRight, Pin } from 'lucide-react';
import ApiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GoldenFunctionStagingModal = ({ pyFile, jsonFile, onConfirm, onCancel }) => {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFunctions, setExpandedFunctions] = useState(new Set());
  const [readOnlyFunctions, setReadOnlyFunctions] = useState(new Set());
  const { session } = useAuth();

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await ApiService.previewGoldenFunctions(
          pyFile,
          jsonFile,
          session?.access_token
        );
        
        setPreviewData(result);
      } catch (err) {
        setError(err.message || 'Failed to load preview');
        console.error('Preview error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pyFile && jsonFile) {
      loadPreview();
    }
  }, [pyFile, jsonFile, session]);

  const handleConfirm = () => {
    if (previewData && onConfirm) {
      // Pass read-only function selections along with preview data
      const confirmData = {
        ...previewData,
        readOnlyFunctions: Array.from(readOnlyFunctions)
      };
      onConfirm(confirmData);
    }
  };

  const toggleReadOnly = (functionName) => {
    const newReadOnly = new Set(readOnlyFunctions);
    if (newReadOnly.has(functionName)) {
      newReadOnly.delete(functionName);
    } else {
      newReadOnly.add(functionName);
    }
    setReadOnlyFunctions(newReadOnly);
  };

  const toggleAllMapped = () => {
    const mappedFunctions = previewData.functions
      .filter(func => func.io_metadata && Object.keys(func.io_metadata).length > 0 && func.name !== 'main')
      .map(func => func.name);
    
    const allMappedSelected = mappedFunctions.every(name => readOnlyFunctions.has(name));
    const newReadOnly = new Set(readOnlyFunctions);
    
    if (allMappedSelected) {
      // Deselect all mapped functions (excluding main)
      mappedFunctions.forEach(name => newReadOnly.delete(name));
    } else {
      // Select all mapped functions (excluding main)
      mappedFunctions.forEach(name => newReadOnly.add(name));
    }
    
    setReadOnlyFunctions(newReadOnly);
  };

  const toggleAllUnmapped = () => {
    const unmappedFunctions = previewData.functions
      .filter(func => (!func.io_metadata || Object.keys(func.io_metadata).length === 0) && func.name !== 'main')
      .map(func => func.name);
    
    const allUnmappedSelected = unmappedFunctions.every(name => readOnlyFunctions.has(name));
    const newReadOnly = new Set(readOnlyFunctions);
    
    if (allUnmappedSelected) {
      // Deselect all unmapped functions
      unmappedFunctions.forEach(name => newReadOnly.delete(name));
    } else {
      // Select all unmapped functions
      unmappedFunctions.forEach(name => newReadOnly.add(name));
    }
    
    setReadOnlyFunctions(newReadOnly);
  };

  const toggleFunction = (functionName) => {
    const newExpanded = new Set(expandedFunctions);
    if (newExpanded.has(functionName)) {
      newExpanded.delete(functionName);
    } else {
      newExpanded.add(functionName);
    }
    setExpandedFunctions(newExpanded);
  };

  const formatIOSpec = (spec) => {
    if (!spec) return 'N/A';
    
    if (Array.isArray(spec)) {
      return spec.map((item, idx) => (
        <div key={idx} className="text-xs">
          {Array.isArray(item) ? (
            <div>
              Tuple[{item.map((subItem, subIdx) => (
                <span key={subIdx}>
                  {subIdx > 0 && ', '}
                  {JSON.stringify(subItem.shape)} ({subItem.dtype})
                </span>
              ))}]
            </div>
          ) : (
            <span>{JSON.stringify(item.shape)} ({item.dtype})</span>
          )}
        </div>
      ));
    } else {
      return <span className="text-xs">{JSON.stringify(spec.shape)} ({spec.dtype})</span>;
    }
  };

  const PinnedFunctionCard = ({ func }) => {
    const isExpanded = expandedFunctions.has(func.name);
    
    return (
      <div className="border rounded-lg border-blue-200 bg-blue-50 opacity-75">
        {/* Pinned Function Header */}
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => toggleFunction(func.name)}
            className="flex-1 flex items-center justify-between hover:bg-opacity-80 transition-colors min-w-0"
          >
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <Pin className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span 
                className="font-mono font-medium text-blue-900 truncate max-w-xs" 
                title={func.name}
                style={{ maxWidth: '300px' }}
              >
                {func.name}
              </span>
              <span className="text-xs text-blue-600 bg-blue-200 px-2 py-1 rounded-full flex-shrink-0">
                Always Editable
              </span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-blue-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-blue-500 flex-shrink-0" />
            )}
          </button>
          
          {/* Disabled Read-Only Checkbox */}
          <div className="ml-3 flex items-center space-x-2 opacity-50">
            <input
              type="checkbox"
              disabled
              className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
            />
            <label className="text-xs text-gray-500 cursor-not-allowed">
              Read-only
            </label>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-blue-200">
            <div className="mb-3">
              <h4 className="text-sm font-medium text-blue-700 mb-2">Entry Point Function:</h4>
              <div className="text-xs text-blue-700 italic">
                This is the main entry point function and is always available for editing
              </div>
            </div>

            {/* Function Code */}
            <div>
              <h4 className="text-sm font-medium text-blue-700 mb-2">Full Function Code:</h4>
              <pre className="bg-blue-100 rounded p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto">
                <code className="text-blue-800">{func.function_code}</code>
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const FunctionCard = ({ func, isMapped }) => {
    const isExpanded = expandedFunctions.has(func.name);
    
    return (
      <div className={`border rounded-lg ${isMapped ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
        {/* Function Header - Clickable to expand */}
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => toggleFunction(func.name)}
            className="flex-1 flex items-center justify-between hover:bg-opacity-80 transition-colors min-w-0"
          >
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              {isMapped ? (
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0" />
              )}
              <span 
                className="font-mono font-medium text-gray-900 truncate max-w-xs" 
                title={func.name}
                style={{ maxWidth: '300px' }}
              >
                {func.name}
              </span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
            )}
          </button>
          
          {/* Read-Only Checkbox */}
          <div className="ml-3 flex items-center space-x-2">
            <input
              type="checkbox"
              id={`readonly-${func.name}`}
              checked={readOnlyFunctions.has(func.name)}
              onChange={(e) => {
                e.stopPropagation();
                toggleReadOnly(func.name);
              }}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label 
              htmlFor={`readonly-${func.name}`}
              className="text-xs text-gray-600 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              Read-only
            </label>
          </div>
        </div>


        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-200">
            {/* I/O Summary */}
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">I/O Specification:</h4>
              {isMapped ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-gray-600">Inputs:</span>
                    <div className="ml-2">{formatIOSpec(func.io_metadata.inputs)}</div>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600">Outputs:</span>
                    <div className="ml-2">{formatIOSpec(func.io_metadata.outputs)}</div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-orange-700 italic">No I/O specification found</div>
              )}
            </div>

            {/* Function Code */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Full Function Code:</h4>
              <pre className="bg-gray-100 rounded p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto">
                <code>{func.function_code}</code>
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Verify Golden Function Mappings</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Analyzing files...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {!loading && !error && previewData && (
            <>
              {/* Validation Warnings */}
              {previewData.validation_warnings?.has_invalid_float_values && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-amber-800 font-medium text-sm">Invalid Float Values Detected</h3>
                      <p className="text-amber-700 text-sm mt-1">
                        The metadata file contains invalid float values (NaN, Infinity) that have been replaced with null values. 
                        These may indicate issues with tensor analysis or model extraction. Please verify the I/O specifications are correct.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* File Info */}
              <div className="bg-gray-50 rounded-md p-4 mb-6">
                <div className="text-sm text-gray-600">
                  <div className="flex items-center mb-1">
                    <FileCode className="h-4 w-4 mr-2" />
                    <span className="font-medium">Python file:</span>
                    <span className="ml-2">{previewData.file_info.filename}</span>
                  </div>
                  <div className="flex items-center">
                    <Database className="h-4 w-4 mr-2" />
                    <span className="font-medium">Metadata file:</span>
                    <span className="ml-2">{previewData.file_info.metadata_filename}</span>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {previewData.mapping_summary.total_functions}
                    </div>
                    <div className="text-sm text-gray-600">Total Functions</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {previewData.mapping_summary.mapped_functions}
                    </div>
                    <div className="text-sm text-gray-600">Mapped to I/O</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">
                      {previewData.mapping_summary.unmapped_functions.length}
                    </div>
                    <div className="text-sm text-gray-600">Unmapped</div>
                  </div>
                </div>

              </div>

              {/* Side-by-Side Function Lists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Mapped Functions */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-700 flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Mapped Functions ({previewData.mapping_summary.mapped_functions})
                    </h3>
                    {previewData.mapping_summary.mapped_functions > 0 && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="select-all-mapped"
                          checked={
                            previewData.functions
                              .filter(func => func.io_metadata && Object.keys(func.io_metadata).length > 0 && func.name !== 'main')
                              .every(func => readOnlyFunctions.has(func.name))
                          }
                          onChange={toggleAllMapped}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label 
                          htmlFor="select-all-mapped"
                          className="text-sm text-gray-600 cursor-pointer"
                        >
                          Select all as read-only
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {/* Pinned Main Function */}
                    {(() => {
                      const mainFunction = previewData.functions.find(func => func.name === 'main');
                      if (mainFunction) {
                        return (
                          <div>
                            <PinnedFunctionCard func={mainFunction} />
                            <div className="flex items-center my-4">
                              <div className="flex-1 border-t border-gray-300"></div>
                              <span className="px-4 text-xs text-gray-500 bg-white">Other Mapped Functions</span>
                              <div className="flex-1 border-t border-gray-300"></div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Regular Mapped Functions (excluding main) */}
                    {previewData.functions
                      .filter(func => func.io_metadata && Object.keys(func.io_metadata).length > 0 && func.name !== 'main')
                      .map((func, index) => (
                        <FunctionCard key={func.name} func={func} isMapped={true} />
                      ))}
                    
                    {/* Show message if no mapped functions */}
                    {previewData.mapping_summary.mapped_functions === 0 && !previewData.functions.find(func => func.name === 'main') && (
                      <div className="text-center py-8 text-gray-500">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No functions have I/O mappings</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Unmapped Functions */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-orange-700 flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2" />
                      Unmapped Functions ({previewData.mapping_summary.unmapped_functions.length})
                    </h3>
                    {previewData.mapping_summary.unmapped_functions.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="select-all-unmapped"
                          checked={
                            previewData.functions
                              .filter(func => (!func.io_metadata || Object.keys(func.io_metadata).length === 0) && func.name !== 'main')
                              .every(func => readOnlyFunctions.has(func.name))
                          }
                          onChange={toggleAllUnmapped}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label 
                          htmlFor="select-all-unmapped"
                          className="text-sm text-gray-600 cursor-pointer"
                        >
                          Select all as read-only
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {previewData.functions
                      .filter(func => (!func.io_metadata || Object.keys(func.io_metadata).length === 0) && func.name !== 'main')
                      .map((func, index) => (
                        <FunctionCard key={func.name} func={func} isMapped={false} />
                      ))}
                    {previewData.mapping_summary.unmapped_functions.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                        <p>All functions are mapped!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || error || !previewData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Confirm & Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoldenFunctionStagingModal;